import { useCallback, useRef } from 'react'
import { makeStore } from '../makeStore'
import { INBOX_CONVERSATION_TAG_DEFAULTS } from './inboxConversationTagDefaults'
import {
    IInboxSettingsState,
    loadInboxSettings,
    resetInboxSettings,
    saveInboxSettings,
    TInboxDefaultView,
} from './inboxSettingsStore'

const ActionType = {
    SET_DEFAULT_VIEW: 'SET_DEFAULT_VIEW',
    SET_CONVERSATION_TAG: 'SET_CONVERSATION_TAG',
    RESET: 'RESET',
} as const

const initialState: IInboxSettingsState = loadInboxSettings(INBOX_CONVERSATION_TAG_DEFAULTS)

const reducer = (state: IInboxSettingsState, action: any): IInboxSettingsState => {
    switch (action.type) {
        case ActionType.SET_DEFAULT_VIEW:
            return { ...state, defaultView: action.payload }
        case ActionType.SET_CONVERSATION_TAG:
            return {
                ...state,
                conversationTags: {
                    ...state.conversationTags,
                    [action.payload.key]: action.payload.value,
                },
            }
        case ActionType.RESET:
            return action.payload
        default:
            return state
    }
}

const { StoreProvider: InboxSettingsStoreProvider, useStoreContext: useInboxSettingsStore } = makeStore(
    reducer,
    initialState,
)

/** Settings UI + persist: dispatch updates store and localStorage. */
export const useInboxSettingsHandlers = () => {
    const { store, dispatch } = useInboxSettingsStore()
    const storeRef = useRef(store)
    storeRef.current = store

    const setDefaultView = useCallback((view: TInboxDefaultView) => {
        const nextState: IInboxSettingsState = { ...storeRef.current, defaultView: view }
        dispatch({ type: ActionType.SET_DEFAULT_VIEW, payload: view })
        saveInboxSettings(nextState)
    }, [dispatch])

    const setConversationTagEnabled = useCallback((key: string, value: boolean) => {
        const nextState: IInboxSettingsState = {
            ...storeRef.current,
            conversationTags: { ...storeRef.current.conversationTags, [key]: value },
        }
        dispatch({ type: ActionType.SET_CONVERSATION_TAG, payload: { key, value } })
        saveInboxSettings(nextState)
    }, [dispatch])

    const resetSettings = useCallback(() => {
        const defaults = resetInboxSettings(INBOX_CONVERSATION_TAG_DEFAULTS)
        dispatch({ type: ActionType.RESET, payload: defaults })
    }, [dispatch])

    return {
        inboxSettings: store,
        setDefaultView,
        setConversationTagEnabled,
        /** @deprecated Use setConversationTagEnabled */
        setLabelEnabled: setConversationTagEnabled,
        resetSettings,
    }
}

export { InboxSettingsStoreProvider, useInboxSettingsStore }
